import { MutableRefObject } from 'react';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 100;

type PollResult = {
  status: string;
  content?: string | null;
  role?: string | null;
  errorReason?: string | null;
  resolvedAlias?: string | null;
  resolvedVersion?: string | null;
};

type PollAgentJobResult = (options: { variables: { jobId: string; partner: string } }) => Promise<{ data?: { pollAgentJobResult?: PollResult | null }; errors?: readonly { message: string }[] }>;

export const useAgentJobPoller = (
  pollAgentJobResult: PollAgentJobResult,
  isMountedRef: MutableRefObject<boolean>
) => {
  const pollJob = async (jobId: string, partner: string): Promise<PollResult> => {
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (!isMountedRef.current) throw new Error('Component unmounted');

      let data: { pollAgentJobResult?: PollResult | null } | undefined;
      try {
        const response = await pollAgentJobResult({ variables: { jobId, partner } });
        if (response.errors?.length) throw new Error(response.errors[0].message);
        data = response.data;
      } catch (err: unknown) {
        const isNetworkError = err instanceof Error && (err as any).networkError != null;
        if (!isNetworkError) throw err;
        if (attempt >= MAX_POLL_ATTEMPTS - 1) throw new Error('Agent job timed out: polling failed after exhausting retries');
        continue;
      }

      const result = data?.pollAgentJobResult;
      if (!result || result.status === 'pending') continue;

      if (result.status === 'success') return result;
      if (result.status === 'failure') throw new Error(result.errorReason || 'Agent job failed');
      throw new Error(`Unexpected job status: ${result.status}`);
    }
    throw new Error('Agent job timed out waiting for response');
  };

  return { pollJob };
};
